/**
 * EWSRiskScoreDonut - Donut chart visualization for risk score
 *
 * Displays a circular donut chart with the risk score in the center.
 * Color varies based on risk level: Green (LOW), Yellow (MEDIUM), Red (HIGH).
 */

import React, { useEffect, useState } from 'react';
import { EWSRiskScoreDonutProps, RiskLevel } from '../../../types/ews.types';

/**
 * Get color configuration based on risk level.
 */
function getRiskColors(level: RiskLevel): { stroke: string; text: string; label: string } {
  switch (level) {
    case 'CRITICAL':
      return {
        stroke: '#991B1B', // red-800
        text: 'text-red-800',
        label: 'Critical Risk',
      };
    case 'HIGH':
      return {
        stroke: '#DC2626', // red-600
        text: 'text-red-600',
        label: 'High Risk',
      };
    case 'MEDIUM':
      return {
        stroke: '#F59E0B', // amber-500
        text: 'text-amber-500',
        label: 'Medium Risk',
      };
    case 'LOW':
    default:
      return {
        stroke: '#16A34A', // green-600
        text: 'text-green-600',
        label: 'Low Risk',
      };
  }
}

export const EWSRiskScoreDonut: React.FC<EWSRiskScoreDonutProps> = ({
  score,
  riskLevel,
  size = 160,
  maxScore = 40, // API uses 0-40 scale
}) => {
  const [animatedScore, setAnimatedScore] = useState(0);

  // Animate the score on mount
  useEffect(() => {
    const duration = 800; // ms
    const steps = 30;
    const increment = score / steps;
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      setAnimatedScore(Math.min(Math.round(increment * currentStep), score));

      if (currentStep >= steps) {
        clearInterval(timer);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [score]);

  const colors = getRiskColors(riskLevel);

  // SVG calculations
  const strokeWidth = 12;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = animatedScore / maxScore; // Use maxScore instead of hardcoded 100
  const strokeDashoffset = circumference * (1 - progress);

  // Center coordinates
  const center = size / 2;

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        {/* Background circle */}
        <svg
          width={size}
          height={size}
          className="transform -rotate-90"
        >
          {/* Gray background track */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="#E5E7EB"
            strokeWidth={strokeWidth}
          />
          {/* Colored progress arc */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={colors.stroke}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            style={{
              transition: 'stroke-dashoffset 0.3s ease-out',
            }}
          />
        </svg>

        {/* Center content - label, score, and subtitle inside donut */}
        <div
          className="absolute inset-0 flex flex-col items-center justify-center"
        >
          <span className={`text-sm font-semibold ${colors.text}`}>
            {colors.label}
          </span>
          <span className={`text-3xl font-bold ${colors.text}`}>
            {animatedScore}/{maxScore}
          </span>
          <span className="text-xs text-gray-400">
            Risk Score
          </span>
        </div>
      </div>
    </div>
  );
};

export default EWSRiskScoreDonut;
